import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlusIcon, 
  TrophyIcon, 
  BriefcaseIcon, 
  AcademicCapIcon, 
  TrashIcon, 
  XMarkIcon,
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';

const AdminCompetitions = () => {
  const [showForm, setShowForm] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  // 1. UPDATED INITIAL STATE: Now uses lowercase types and includes duration_months
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'competition', // Changed from 'Standard'
    fee: 0,
    prize_pool: '',
    start_date: '',
    end_date: '',
    duration_months: 1,  // New field for Internships
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCompetitions();
      if (res.success) setCompetitions(res.competitions);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Create a clean copy of the data
  const payload = {
    ...formData,
    // Ensure fee is never an empty string
        fee: formData.fee === '' ? 0 : parseFloat(formData.fee),
   // Only send duration if it's an internship
    duration_months: formData.type === 'internship' ? parseInt(formData.duration_months) : null,
    // Ensure empty dates are sent as null, not empty strings
    start_date: formData.start_date || null,
    end_date: formData.end_date || null
  };
  try {
    let res;
    if(isEditing){
      res = await adminApi.updateCompetition(currentId, payload);
    } else {
      res = await adminApi.createCompetition(payload);
    }
    
    if (res) {
       setShowForm(false);
 setIsEditing(false);
 fetchData();
 // Reset form to default state
 setFormData({
   title: '', description: '', type: 'competition',
   fee: 0, prize_pool: '', start_date: '',
   end_date: '', duration_months: 1, status: 'active'
 });
 alert(isEditing ? "Updated Successfully!" : "Created Successfully!");
    }
  } catch (err) {
    console.error("Submission error details:", err.response?.data || err.message);
    alert(`Failed: ${err.response?.data?.error || "Check console for details"}`);
  }
};
  
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this mission?")) {
      try {
        const res = await adminApi.deleteCompetition(id);
        if (res.success) fetchData();
      } catch (err) {
        alert("Delete failed");
      }
    }
  };

  const handleEditClick = (comp) => {
    setIsEditing(true);
    setCurrentId(comp.id);
    
    // Format dates for the HTML date input
    const formattedStart = comp.start_date ? new Date(comp.start_date).toISOString().split('T')[0] : '';
    const formattedEnd = comp.end_date ? new Date(comp.end_date).toISOString().split('T')[0] : '';

    setFormData({
      title: comp.title,
      description: comp.description,
      type: comp.type.toLowerCase(), // Ensure it matches our dropdown values
      fee: comp.fee,
      prize_pool: comp.prize_pool,
      start_date: formattedStart,
      end_date: formattedEnd,
      duration_months: comp.duration_months || 1, // Load duration if it exists
      status: comp.status
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 border-b border-gray-800 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight">Program Management</h1>
          <p className="text-gray-400 mt-1">Configure and launch evaluation tracks</p>
        </div>
        <button 
          onClick={() => {
            setShowForm(!showForm);
            if(showForm) setIsEditing(false);
          }}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all shadow-lg ${
            showForm 
            ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700" 
            : "bg-gradient-to-r from-red-600 to-orange-600 text-white hover:scale-105"
          }`}
        >
          {showForm ? <XMarkIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
          {showForm ? "Cancel Mission" : "Launch New Program"}
        </button>
      </div>

      {/* Creation Form Overlay */}
      <AnimatePresence>
        {showForm && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-800 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden mb-10"
          >
            <div className="p-6 border-b border-gray-700 bg-gray-800/50">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <PlusIcon className="h-5 w-5 text-red-500" /> 
                {isEditing ? "Edit Program Details" : "New Program Details"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Competition Title */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Program Title</label>
                <input 
                  type="text" required
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none transition-all"
                  placeholder="e.g. Fullstack Developer Challenge"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>

              {/* 2. UPDATED PROGRAM TYPE SELECT */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Program Type</label>
                <select 
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none transition-all appearance-none"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <option value="competition">Competition Track</option>
                  <option value="internship">Internship Track</option>
                  <option value="job">Career Placement</option>
                </select>
              </div>

              {/* 3. CONDITIONAL INTERNSHIP DURATION FIELD */}
              <AnimatePresence>
                {formData.type === 'internship' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }} 
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="md:col-span-2 space-y-2 p-4 bg-red-500/5 border border-red-500/20 rounded-xl"
                  >
                    <label className="text-xs font-bold text-red-400 uppercase tracking-widest">
                      Internship Duration (After Evaluation)
                    </label>
                    <select 
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white outline-none focus:border-red-500 transition-all"
                      value={formData.duration_months}
                      onChange={(e) => setFormData({...formData, duration_months: e.target.value})}
                    >
                      <option value={1}>1 Month Duration</option>
                      <option value={2}>2 Months Duration</option>
                      <option value={3}>3 Months Duration</option>
                    </select>
                    <p className="text-[10px] text-gray-500 italic font-medium">
                      * This duration will be applied to the student's certificate upon successful completion.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Description */}
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Brief Description</label>
                <textarea 
                  required className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none h-24"
                  placeholder="What is required to win? What are the key stages?"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                ></textarea>
              </div>

              {/* Entry Fee */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Entry Fee (INR)</label>
                <div className="relative">
                  <span className="absolute left-4 top-3.5 font-bold text-gray-500">₹</span>
                  <input 
                    type="number" 
                    className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white outline-none focus:border-red-500"
                    placeholder="0"
                    value={formData.fee}
                    onChange={(e) => setFormData({...formData, fee: e.target.value})} />
                </div>
              </div>

              {/* Reward Pool */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Reward / Prize Pool</label>
                <input 
                  type="text" className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none"
                  placeholder="e.g. ₹1000 + Internship Certificate"
                  value={formData.prize_pool}
                  onChange={(e) => setFormData({...formData, prize_pool: e.target.value})}
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 md:col-span-2">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Evaluation Start Date</label>
                  <input
                    type="date" required
                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Evaluation End Date</label>
                  <input
                    type="date" required
                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-red-500 outline-none"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="md:col-span-2">
                <button type="submit" className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-black rounded-xl hover:opacity-90 transition-all shadow-xl shadow-red-600/20">
                  {isEditing ? "UPDATE MISSION" : "DEPLOY MISSION"}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List Section */}
      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 animate-pulse font-mono text-sm">SECURELY FETCHING DATA...</p>
          </div>
        ) : (
          competitions.map((comp) => (
            <div key={comp.id} className="group bg-gray-800 p-5 rounded-2xl border border-gray-700 hover:border-red-500/50 transition-all flex flex-col md:flex-row justify-between items-center gap-4 shadow-lg">
              <div className="flex items-center gap-5 w-full">
                <div className={`p-4 rounded-xl ${
                  comp.type.toLowerCase() === 'internship' ? 'bg-purple-500/10 text-purple-400' : 
                  comp.type.toLowerCase() === 'job' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                }`}>
                  {comp.type.toLowerCase() === 'internship' ? <AcademicCapIcon className="h-7 w-7"/> : 
                   comp.type.toLowerCase() === 'job' ? <BriefcaseIcon className="h-7 w-7"/> : <TrophyIcon className="h-7 w-7"/>}
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white group-hover:text-red-400 transition-colors">{comp.title}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] font-black uppercase bg-gray-700 px-2 py-0.5 rounded text-gray-300 tracking-tighter">
                      {comp.type} {comp.duration_months && `(${comp.duration_months}m)`}
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1 font-medium">
                      <span className='text-red-400 font-bold'>₹</span>
                      {Number(comp.fee) === 0 ? 'FREE' : comp.fee}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 w-full md:w-auto">
                <button 
                  className="flex-1 md:flex-none px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl text-sm font-bold transition-all border border-gray-600"
                  onClick={() => handleEditClick(comp)}
                >
                  Edit
                </button>
                <button 
                  className="flex-1 md:flex-none p-2.5 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all border border-red-500/20"
                  onClick={() => handleDelete(comp.id)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminCompetitions;